<template>
  <div class="solution">
    <!-- 使用公共导航栏组件 -->
    <common-header
      :is-scrolled="isScrolled"
      :current-section="currentSection"
      @search="handleSearch"
      @lang-change="switchLang"
      @section-change="handleSectionChange"
      @scroll-to-section="scrollToSection"
    />

    <!-- 页面banner -->
    <div class="page-banner">
      <div class="banner-content">
        <h1 class="animate-fade-in">{{ $t('solution.title') }}</h1>
        <p class="animate-fade-in-delay" v-if="$i18n.locale === 'zh'">{{ $t('solution.subtitle') }}</p>
      </div>
    </div>

    <!-- 子目录导航条 -->
    <div class="solution-subnav" :class="{ 'subnav-scrolled': isScrolled }">
      <div class="subnav-inner">
        <ul>
          <li 
            v-for="section in sections" 
            :key="section.id"
            :class="{ active: currentSection === section.id }"
          >
            <a href="#" @click.prevent="scrollToSection(section.id)">{{ $t(`solution.${section.id}.title`) }}</a>
          </li>
        </ul>
      </div>
    </div>

    <!-- 内容区域 -->
    <div class="content-container">
      <!-- 火电无人值守部分 -->
      <section id="unmanned" class="content-section" :class="{ 'section-visible': isVisible.unmanned }">
        <div class="section-content" style="max-width: 1400px;">
          <h2 class="animate-slide-up">{{ $t('solution.unmanned.title') }}</h2>
          <div class="solution-subtitle animate-slide-up-delay" v-if="$i18n.locale === 'zh'">{{ $t('solution.unmanned.subtitle') }}</div>
          <div class="solution-content" v-if="solutionVisibility.unmanned">
            <!-- 斗轮机无人值守系统 -->
            <div class="solution-part">
              <div class="solution-text animate-fade-in">
                <h3>{{ $t('solution.unmanned.bucketWheel.title') }}</h3>
                <p>{{ $t('solution.unmanned.p1') }}</p>
              </div>
              <div class="solution-images animate-slide-left">
                <img src="@/assets/production/jie1.png" :alt="$t('solution.unmanned.bucketWheel.title')" class="image-half" />
                <img src="@/assets/production/jie2.png" :alt="$t('solution.unmanned.bucketWheel.title')" class="image-half" />
              </div>
            </div>

            <!-- 圆形料场智能管控系统 -->
            <div class="solution-part">
              <div class="solution-text animate-fade-in">
                <h3>{{ $t('solution.unmanned.circularStockyard.title') }}</h3>
                <p>{{ $t('solution.unmanned.p2') }}</p>
              </div>
              <div class="solution-images animate-slide-left">
                <img src="@/assets/production/jie3.png" :alt="$t('solution.unmanned.circularStockyard.title')" class="image-half" />
                <img src="@/assets/production/jie4.png" :alt="$t('solution.unmanned.circularStockyard.title')" class="image-half" />
              </div>
            </div>

            <!-- 半门式刮板堆取料机无人值守系统 -->
            <div class="solution-part">
              <div class="solution-text animate-fade-in">
                <h3>{{ $t('solution.unmanned.semiGantry.title') }}</h3>
                <p>{{ $t('solution.unmanned.p3') }}</p>
              </div>
              <div class="solution-images animate-slide-left">
                <img src="@/assets/production/jie5.png" :alt="$t('solution.unmanned.semiGantry.title')" class="image-half" />
                <img src="@/assets/production/jie6.png" :alt="$t('solution.unmanned.semiGantry.title')" class="image-half" />
              </div>
            </div>
          </div>
          <div v-else class="no-data">
            <p>{{ $t('common.noData') }}</p>
          </div>
        </div>
      </section>

      <!-- 燃料智能化部分 -->
      <section id="fuel" class="content-section" :class="{ 'section-visible': isVisible.fuel }">
        <div class="section-content">
          <h2 class="animate-slide-up">{{ $t('solution.fuel.title') }}</h2>
          <div class="solution-subtitle animate-slide-up-delay" v-if="$i18n.locale === 'zh'">{{ $t('solution.fuel.subtitle') }}</div>
          <div class="solution-content" v-if="solutionVisibility.fuel">
            <div class="solution-part">
              <div class="solution-text animate-fade-in">
                <p>{{ $t('solution.fuel.p1') }}</p>
              </div>
              <div class="solution-images animate-slide-left">
                <img src="@/assets/production/2.png" :alt="$t('solution.fuel.title')" class="image-half" />
                <img src="@/assets/production/21.png" :alt="$t('solution.fuel.title')" class="image-half" />
              </div>
            </div>
          </div>
          <div v-else class="no-data">
            <p>{{ $t('common.noData') }}</p>
          </div>
        </div>
      </section>

      <!-- 卸船AI部分 -->
      <section id="unloading" class="content-section" :class="{ 'section-visible': isVisible.unloading }">
        <div class="section-content">
          <h2 class="animate-slide-up">{{ $t('solution.unloading.title') }}</h2>
          <div class="solution-subtitle animate-slide-up-delay" v-if="$i18n.locale === 'zh'">{{ $t('solution.unloading.subtitle') }}</div>
          <div class="solution-content" v-if="solutionVisibility.unloading">
            <div class="solution-part">
              <div class="solution-text animate-fade-in">
                <p>{{ $t('solution.unloading.p1') }}</p>
              </div>
              <div class="solution-images animate-slide-left">
                <img src="@/assets/production/aixie.png" :alt="$t('solution.unloading.title')" class="single-image" />
              </div>
            </div>
          </div>
          <div v-else class="no-data">
            <p>{{ $t('common.noData') }}</p>
          </div>
        </div>
      </section>

      <!-- 皮带巡检部分 -->
      <section id="inspection" class="content-section" :class="{ 'section-visible': isVisible.inspection }">
        <div class="section-content">
          <h2 class="animate-slide-up">{{ $t('solution.inspection.title') }}</h2>
          <div class="solution-subtitle animate-slide-up-delay" v-if="$i18n.locale === 'zh'">{{ $t('solution.inspection.subtitle') }}</div>
          <div class="solution-content" v-if="solutionVisibility.inspection">
            <div class="solution-part">
              <div class="solution-text animate-fade-in">
                <p>{{ $t('solution.inspection.p1') }}</p>
              </div>
              <div class="solution-images animate-slide-left">
                <img src="@/assets/production/pidai1.png"  class="image-half" />
                <img src="@/assets/production/pidai2.png"  class="image-half" />
              </div>
            </div>
          </div>
          <div v-else class="no-data">
            <p>{{ $t('common.noData') }}</p>
          </div>
        </div>
      </section>

      <!-- 核心技术部分 -->
      <section id="coreTech" class="content-section" :class="{ 'section-visible': isVisible.coreTech }">
        <div class="section-content">
          <h2 class="animate-slide-up">{{ $t('solution.coreTech.title') }}</h2>
          <div class="solution-subtitle animate-slide-up-delay" v-if="$i18n.locale === 'zh'">{{ $t('solution.coreTech.subtitle') }}</div>
          <div class="solution-content" v-if="solutionVisibility.coreTech">
            <div class="solution-part">
              <div class="solution-text animate-fade-in">
                <p>{{ $t('solution.coreTech.p1') }}</p>
              </div>
              <div class="core-tech-grid animate-slide-left">
                <div class="tech-item">
                  <h3>{{ $t('solution.coreTech.vision.title') }}</h3>
                  <p>{{ $t('solution.coreTech.vision.desc') }}</p>
                </div>
                <div class="tech-item">
                  <h3>{{ $t('solution.coreTech.ai.title') }}</h3>
                  <p>{{ $t('solution.coreTech.ai.desc') }}</p>
                </div>
                <div class="tech-item">
                  <h3>{{ $t('solution.coreTech.control.title') }}</h3>
                  <p>{{ $t('solution.coreTech.control.desc') }}</p>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="no-data">
            <p>{{ $t('common.noData') }}</p>
          </div>
        </div>
      </section>
    </div>

    <!-- 使用 CommonSection 组件显示页脚 -->
    <common-section type="footer" id="contact" @scroll-to-section="scrollToSection" />
  </div>
</template>

<script>
import CommonHeader from '@/components/CommonHeader.vue'
import CommonSection from '@/components/CommonSection.vue'

export default {
  name: 'SolutionView',
  components: {
    CommonHeader,
    CommonSection
  },
  data() {
    return {
      currentSection: 'unmanned',
      currentLang: 'CN',
      sectionLock: false,
      scrollUnlockTimer: null,
      scrollRAF: null,
      scrollTarget: null,
      isScrolled: false,
      sections: [
        { id: 'unmanned', title: '散料无人值守' },
        { id: 'fuel', title: '燃料智能化' },
        { id: 'unloading', title: '卸船AI' },
        { id: 'inspection', title: '皮带巡检' },
        { id: 'coreTech', title: '核心技术' }
      ],
      isVisible: {
        unmanned: true,
        fuel: false,
        unloading: false,
        inspection: false,
        coreTech: false
      },
      observer: null
    }
  },
  computed: {
    solutionVisibility() {
      return {
        unmanned: this.$i18n.messages[this.$i18n.locale].solution.unmanned.visible,
        fuel: this.$i18n.messages[this.$i18n.locale].solution.fuel.visible,
        unloading: this.$i18n.messages[this.$i18n.locale].solution.unloading.visible,
        inspection: this.$i18n.messages[this.$i18n.locale].solution.inspection.visible,
        coreTech: this.$i18n.messages[this.$i18n.locale].solution.coreTech.visible
      }
    }
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll);
    this.checkHash();
    this.setupIntersectionObserver();
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll);
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this.scrollRAF) {
      cancelAnimationFrame(this.scrollRAF);
    }
  },
  methods: {
    handleSearch(searchText) {
      if (searchText.trim()) {
        console.log('Searching for:', searchText);
      }
    },
    switchLang(lang) {
      this.currentLang = lang;
    },
    handleSectionChange(section) {
      this.currentSection = section;
    },
    handleScroll() {
      if (this.scrollRAF) {
        cancelAnimationFrame(this.scrollRAF);
      }
      
      this.scrollRAF = requestAnimationFrame(() => {
        // 判断是否滚动到 page-banner 底部
        const bannerEl = document.querySelector('.page-banner');
        if (bannerEl) {
          const bannerBottom = bannerEl.getBoundingClientRect().bottom;
          this.isScrolled = window.scrollY > bannerBottom;
        }

        // 如果正在滚动到目标section，不更新状态
        if (this.sectionLock || this.scrollTarget) {
          return;
        }

        const sections = ['unmanned', 'fuel', 'unloading', 'inspection', 'coreTech'];
        const offset = 192;
        let currentSection = this.currentSection;
        let minDistance = Infinity;

        const sectionRects = sections.map(section => {
          const element = document.getElementById(section);
          return element ? {
            section,
            rect: element.getBoundingClientRect()
          } : null;
        }).filter(Boolean);

        // 检查是否在最后一个section之后
        const lastSection = sectionRects[sectionRects.length - 1];
        if (lastSection && lastSection.rect.top < offset) {
          currentSection = lastSection.section;
        } else {
          // 否则检查当前视口中的section
          sectionRects.forEach(({ section, rect }) => {
            if (rect.top <= offset) {
              const distance = Math.abs(rect.top - offset);
              if (distance < minDistance) {
                minDistance = distance;
                currentSection = section;
              }
            }
          });
        }

        if (currentSection !== this.currentSection) {
          this.currentSection = currentSection;
          if (window.location.hash !== `#${currentSection}`) {
            history.replaceState(null, null, `#${currentSection}`);
          }
        }
      });
    },
    checkHash() {
      const hash = window.location.hash.slice(1);
      if (hash && ['unmanned', 'fuel', 'unloading', 'inspection', 'coreTech'].includes(hash)) {
        this.currentSection = hash;
        this.$nextTick(() => {
          const element = document.getElementById(hash);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        });
      }
    },
    scrollToSection(section) {
      const element = document.getElementById(section);
      if (element) {
        const offset = 180;
        const top = element.getBoundingClientRect().top + window.pageYOffset - offset;
        
        // 立即更新当前section状态
        this.currentSection = section;
        this.scrollTarget = section;
        this.sectionLock = true;
        
        requestAnimationFrame(() => {
          window.scrollTo({ top, behavior: 'smooth' });
          
          if (window.location.hash !== `#${section}`) {
            history.replaceState(null, null, `#${section}`);
          }
          
          // 监听滚动结束
          const checkScrollEnd = () => {
            const currentTop = window.pageYOffset;
            if (Math.abs(currentTop - top) < 5) { // 滚动到目标位置附近
              this.scrollTarget = null;
              this.sectionLock = false;
            } else {
              requestAnimationFrame(checkScrollEnd);
            }
          };
          
          // 开始检查滚动结束
          setTimeout(checkScrollEnd, 300);
        });
      }
    },
    setupIntersectionObserver() {
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const sectionId = entry.target.id;
            this.$set(this.isVisible, sectionId, true);
            // 一旦显示就不再观察
            this.observer.unobserve(entry.target);
          }
        });
      }, {
        threshold: 0.1,
        rootMargin: '-100px 0px'
      });

      // 观察所有内容部分
      ['unmanned', 'fuel', 'unloading', 'inspection', 'coreTech'].forEach(sectionId => {
        const element = document.getElementById(sectionId);
        if (element) {
          this.observer.observe(element);
        }
      });
    }
  }
}
</script>

<style lang="scss" scoped>
.solution {
  // 复用about页面的基础样式
  ::-webkit-scrollbar {
    width: 8px;
    height: 0;
  }

  ::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }

  ::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;
    transition: all 0.3s ease;

    &:hover {
      background: #a8a8a8;
    }
  }

  .content-container {
    width: 100%;
    max-width: 100vw;
    overflow-x: hidden;
    padding: 60px 0;
    scroll-behavior: smooth;
  }

  .section-content {
    width: 100%;
    max-width: 1200px;
    margin: 30px auto;
    padding: 0 20px;
    box-sizing: border-box;
  }

  // 页面banner样式
  .page-banner {
    height: 65vh;
    background: url('@/assets/banners/solution-banner.jpg');
    background-size: cover;
    background-position: center;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    text-align: center;

    .banner-content {
      padding-right: 52%;
      h1 {
        font-size: 48px;
        margin-bottom: 15px;
        font-weight: 600;
      }

      p {
        font-size: 24px;
        opacity: 0.9;
      }
    }
  }

  // 导航栏样式
  .solution-subnav {
    width: 100%;
    background: transparent;
    position: sticky;
    top: 100px;
    z-index: 100;
    display: flex;
    justify-content: center;
    height: 93px;
    transition: all 0.3s ease;

    @media (max-width: 1200px) {
      display: none;
    }
    &.subnav-scrolled {
      background: #fff;
      box-shadow: 0 2px 8px rgba(0,0,0,0.03);
    }

    .subnav-inner {
      width: 80%;
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0;
    }

    ul {
      width: 100%;
      height: 100%;
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0;
      margin: 0;
      list-style: none;
    }

    li {
      flex: 1 1 0;
      text-align: center;
      position: relative;
      height: 54px;

      a {
        display: block;
        font-size: 18px;
        color: #333;
        padding: 10px 0;
        transition: all 0.3s ease;
        text-decoration: none;
        position: relative;

        &::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 50%;
          width: 0;
          height: 3px;
          background: linear-gradient(90deg, #a2ca07, #009fd9);
          transition: all 0.3s ease;
          transform: translateX(-50%);
          border-radius: 2px;
          opacity: 0;
        }
      }

      &.active a {
        color: #1d2088;
        font-weight: 600;

        &::after {
          width: 60%;
          opacity: 1;
        }
      }

      a:hover {
        color: #1d2088;
        font-weight: 600;

        &::after {
          width: 60%;
          opacity: 1;
        }
      }
    }
  }

  // 内容区域基础样式
  .content-section {
    padding: 40px 0;
    
    h2 {
      font-size: 36px;
      color: #333;
      margin-bottom: 40px;
      text-align: center;
    }
  }

  // 解决方案内容样式
  .solution-content {
    display: flex;
    flex-direction: column;
    gap: 90px;
    margin-bottom: 40px;
  }

  .solution-part {
    display: flex;
    flex-direction: column;
    gap: 30px;
    width: 100%;
  }

  .solution-text {
    h3 {
      font-size: 28px;
      font-weight: 600;
      margin-bottom: 20px;
      color: #1d2088;
    }

    p {
      font-size: 16px;
      line-height: 1.8;
      color: #666;
      text-align: justify;
    }
  }

  .solution-images {
    display: flex;
    gap: 20px;
    width: 100%;

    .image-half {
      width: calc(50% - 10px);
      height: auto;
      object-fit: cover;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.1);
      transition: transform 0.3s ease;

      &:hover {
        transform: translateY(-5px);
      }
    }

    .single-image {
      width: 100%;
      height: auto;
      object-fit: cover;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.1);
      transition: transform 0.3s ease;

      &:hover {
        transform: translateY(-5px);
      }
    }
  }

  .solution-right img {
    width: 100%;
    height: auto;
    object-fit: cover;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  }

  // 响应式设计
  @media (max-width: 768px) {
    .page-banner {
      height: 300px;
      .banner-content {
        h1 {
          font-size: 36px;
        }
        p {
          font-size: 18px;
        }
      }
    }

    .solution-subnav {
      height: auto;
      padding: 10px 0;
      
      .subnav-inner {
        width: 100%;
        padding: 0 20px;
      }
      
      ul {
        flex-wrap: wrap;
      }
      
      li {
        flex: 0 0 50%;
        margin-bottom: 10px;
        height: 40px;

        a {
          font-size: 16px;
          padding: 5px 0;
        }
      }
    }

    .content-section {
      padding: 40px 0;
      
      h2 {
        font-size: 28px;
        margin-bottom: 30px;
      }
    }
  }

  @media (max-width: 1200px) {
    .solution-content {
      gap: 60px;
    }
  }

  @media (max-width: 992px) {
    .solution-content {
      gap: 60px;
    }

    .solution-part {
      gap: 20px;
    }

    .solution-text {
      h3 {
        font-size: 24px;
      }
    }

    .solution-images {
      flex-direction: column;
      gap: 15px;

      .image-half {
        width: 100%;
      }
    }
  }

  .solution-subtitle {
    font-size: 24px;
    color: #666;
    text-align: center;
    margin-bottom: 40px;
    font-weight: 500;
  }

  .no-data {
    text-align: center;
    padding: 40px 0;
    color: #333;
    font-size: 22px;
    background: #f9f9f9;
    border-radius: 8px;
    margin: 20px 0;
  }

  // 添加动画相关样式
  .animate-fade-in {
    opacity: 0;
    animation: fadeIn 1s ease forwards;
  }

  .animate-fade-in-delay {
    opacity: 0;
    animation: fadeIn 1s ease 0.3s forwards;
  }

  .animate-slide-up {
    opacity: 0;
    transform: translateY(30px);
    animation: slideUp 0.8s ease forwards;
  }

  .animate-slide-up-delay {
    opacity: 0;
    transform: translateY(30px);
    animation: slideUp 0.8s ease 0.3s forwards;
  }

  .animate-slide-left {
    opacity: 0;
    transform: translateX(50px);
    animation: slideLeft 0.8s ease forwards;
  }

  .content-section {
    opacity: 0;
    transform: translateY(20px);
    transition: opacity 0.6s ease, transform 0.6s ease;
    
    &.section-visible {
      opacity: 1;
      transform: translateY(0);
    }
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideLeft {
  from {
    opacity: 0;
    transform: translateX(50px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.solution-part {
  display: flex;
  flex-direction: column;
  gap: 30px;
  width: 100%;
}

.solution-part:last-child {
  margin-bottom: 0;
}

.solution-part .solution-left {
  flex: 1;
}

.solution-part .solution-right {
  flex: 1;
  display: flex;
  gap: 1rem;
}

.solution-part .solution-right img {
  width: 100%;
  height: auto;
  object-fit: cover;
  border-radius: 8px;
}

.solution-text h3 {
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: var(--primary-color);
}

@media (max-width: 768px) {
  .solution-part {
    flex-direction: column;
  }
  
  .solution-part .solution-right {
    margin-top: 1rem;
  }
}

.core-tech-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  margin-top: 40px;

  .tech-item {
    background: #fff;
    padding: 30px;
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
    transition: transform 0.3s ease;

    &:hover {
      transform: translateY(-5px);
    }

    h3 {
      font-size: 20px;
      color: #1d2088;
      margin-bottom: 15px;
      font-weight: 600;
    }

    p {
      font-size: 16px;
      line-height: 1.6;
      color: #666;
    }
  }
}

@media (max-width: 992px) {
  .core-tech-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .core-tech-grid {
    grid-template-columns: 1fr;
  }
}
</style> 